import { useState } from 'react';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { useInstallPrompt, type InstallMode } from './installPrompt';

/* raw-ok: SVG grid/stroke spec in prose, not a CSS literal — 24px grid, 2px stroke, currentColor
   (mirrors shell/glyphs.tsx's own convention). §11.11 requires SVG glyphs here, never emoji. */
const ShareGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M8 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    <path
      d="M5 11v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"
      stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
    />
  </svg>
);

const AddToHomeGlyph = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export type InstallSheetViewProps = {
  mode: NonNullable<InstallMode>;
  onInstall: () => void;
  onDismiss: () => void;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
};

/**
 * The pure, presentational half of the install prompt — no hook, no global store, just the two
 * modes' markup driven entirely by props. Split out from `InstallSheet` (below) specifically so
 * the gallery can show BOTH the 'native' and 'ios' states side by side: `useInstallPrompt`'s
 * `mode`/`eligible` come from one module-level store (`useInstallStore` — same "module-level,
 * survives independent of any one component" shape as `wardrobe/useOutfitDraft.ts`), which can
 * only hold ONE value at a time — two simultaneously-rendered connected `<InstallSheet />`s on
 * one static gallery page could never show two different modes at once, but two
 * `<InstallSheetView mode="…">`s with fixed props can.
 */
export function InstallSheetView({ mode, onInstall, onDismiss, portalContainer }: InstallSheetViewProps) {
  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onDismiss(); }}
      contentClassName="install-sheet"
      portalContainer={portalContainer}
    >
      {mode === 'native' ? (
        <>
          <Dialog.Title asChild>
            <h2>Install Project Pets?</h2>
          </Dialog.Title>
          <Dialog.Description>
            Add Project Pets to your home screen: a full-screen app, instant launch, no browser bar.
          </Dialog.Description>
          <div className="actions install-sheet-actions">
            <button type="button" className="btn-primary" onClick={onInstall}>
              Install
            </button>
            <button type="button" className="btn-quiet" onClick={onDismiss}>
              Not now
            </button>
          </div>
        </>
      ) : (
        <>
          <Dialog.Title asChild>
            <h2>Add Project Pets to your Home Screen</h2>
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Two steps to install Project Pets on iOS Safari: open the Share menu, then choose Add to
            Home Screen.
          </Dialog.Description>
          <ol className="install-steps">
            <li>
              <ShareGlyph />
              <span>Tap the Share button in Safari&rsquo;s toolbar.</span>
            </li>
            <li>
              <AddToHomeGlyph />
              <span>Scroll down and choose &ldquo;Add to Home Screen&rdquo;.</span>
            </li>
          </ol>
          <div className="actions install-sheet-actions">
            <button type="button" className="btn-primary" onClick={onDismiss}>
              Got it
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

/**
 * §11.11 / SCREENS.md "PWA — Install Prompt". Mounted once in `App.tsx`, always in the tree —
 * `useInstallPrompt().eligible` (already the whole trigger + cap + not-installed + prompt-
 * possible check) is the only thing deciding whether it renders anything at all, so there's
 * nothing here that could show up on a first visit with no trigger (§11.11: "never fire on
 * first visit").
 *
 * Native (`mode === 'native'`): the in-app PRE-prompt itself — "Install Project Pets?" with an
 * Install / Not now choice. Accepting is what fires the REAL, one-shot browser dialog
 * (`promptNow`, Chromium's stashed `deferredPrompt.prompt()`) — never the other way around, per
 * §11.11 "the OS dialog fires only after the in-app pre-prompt acceptance." `promptNow` itself
 * clears the stashed event as its first step, so `mode` flips to `null` (or `'ios'`, never
 * applicable on the same device) the instant Install is tapped — this sheet unmounts on its own
 * right as the real OS dialog takes over, with no separate close call needed here.
 *
 * iOS Safari (`mode === 'ios'`) has no such OS API at all — `beforeinstallprompt` doesn't exist
 * there — so this sheet IS the whole prompt: illustrated Add-to-Home-Screen steps, dismissed via
 * "Got it". Per §11.11's own framing, this doubles as the notification pre-prompt on iOS, since
 * iOS web push only works once installed to the Home Screen — but Phase 1 doesn't touch push at
 * all (Phase 3, per FABLE5_BUILD_PROMPT.md §11.11), so that's future context, not anything this
 * component does yet.
 *
 * `hiddenThisMount`: SCREENS.md's own state table has a `shown` → `dismissed` transition
 * distinct from the underlying "asks so far" cap (`installPrompt.ts`'s `useInstallStore.asks`,
 * capped at 2 lifetime) — dismissing once (`asks` 0→1) does NOT by itself make
 * `useInstallPrompt().eligible` false (that cap is 2, not 1), so without this local flag the
 * sheet would immediately re-render itself right back open on the very next tick, since nothing
 * else changed. This is ordinary component-local state (resets on a fresh page load, i.e. the
 * next genuine app session) precisely because §11.11's "ask at most twice, ever" is about total
 * asks across the account's lifetime, not "never show again after this one render" — a second,
 * later ask on a future qualifying moment is still allowed and still wanted.
 */
export function InstallSheet() {
  const { eligible, mode, promptNow, dismiss } = useInstallPrompt();
  const [hiddenThisMount, setHiddenThisMount] = useState(false);

  if (!eligible || !mode || hiddenThisMount) return null;

  function handleInstall() {
    setHiddenThisMount(true);
    void promptNow();
  }

  function handleDismiss() {
    setHiddenThisMount(true);
    dismiss();
  }

  return <InstallSheetView mode={mode} onInstall={handleInstall} onDismiss={handleDismiss} />;
}
